import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { HydrateContent } from "@foundation/hydrate-model";
import {
  IntradayDataPoint,
  Periodicity,
  type ChartDateRange,
} from "@bloomberg/octagon-shared";
import type { TickerSeries } from "~types/types";
import {
  fetchIntradayChartData,
  fetchIntradayChartDataWithDates,
} from "~api/chart-data/fetchIntradayChartData";
import hydrateArray from "~transforms/hydrateArray";
import { tickerChart } from "~models/TickerChart";
import { fetchHistoryChartData } from "~api/chart-data/fetchHistoryChartData";
import { getChartConfig, getYTDInDays } from "~utils/chart/getChartConfig";
import { getChartDates } from "~utils/chart/getChartDates";

const CHART_REFERENCE_FIELDS = [
  // issuedCurrency
  "DS004",
  // lastUpdate
  "UTIME",
  // longName
  "DS520",
  // marketStatus
  "PR286",
  // name
  "DS002",
  // previousClosingPriceOneTradingDayAgo
  "PR050",
];

const INTRADAY_OHLC_DATA_POINTS = [
  IntradayDataPoint.Open,
  IntradayDataPoint.High,
  IntradayDataPoint.Low,
  IntradayDataPoint.Close,
  IntradayDataPoint.Volume,
].join(",") as IntradayDataPoint;

const INTRADAY_VOLUME_DATA_POINTS = [
  IntradayDataPoint.Close,
  IntradayDataPoint.Volume,
].join(",") as IntradayDataPoint;

const HISTORY_OHLC_FIELDS = [
  "PX_OPEN",
  "PX_HIGH",
  "PX_LOW",
  "PX_LAST",
  "VOLUME",
].join(",");

function getIntradayDataPoints(
  isOhlcv: boolean,
  includeVolume: boolean
): IntradayDataPoint {
  if (isOhlcv) return INTRADAY_OHLC_DATA_POINTS;
  if (includeVolume) return INTRADAY_VOLUME_DATA_POINTS;

  return IntradayDataPoint.Close;
}

function getHistoryFields(isOhlcv: boolean, includeVolume: boolean): string {
  if (isOhlcv) return HISTORY_OHLC_FIELDS;
  if (includeVolume) return "PX_LAST,VOLUME";

  return "PX_LAST";
}

function fetchIntradayData(
  tickers: string[],
  chartDateRange: ChartDateRange,
  isOhlcv: boolean,
  includeVolume: boolean,
  intervalMinutes: number,
  tradingDayCount?: number
): Promise<unknown> {
  const referenceFields = CHART_REFERENCE_FIELDS.join(",");
  const dataPoints = getIntradayDataPoints(isOhlcv, includeVolume);

  if (tradingDayCount) {
    return fetchIntradayChartData(
      tickers,
      tradingDayCount,
      dataPoints,
      intervalMinutes,
      referenceFields
    );
  }

  const { startDate, endDate } = getChartDates(chartDateRange);

  return fetchIntradayChartDataWithDates(
    tickers,
    startDate,
    endDate,
    dataPoints,
    intervalMinutes,
    referenceFields
  );
}

function fetchHistoryData(
  tickers: string[],
  chartDateRange: ChartDateRange,
  isOhlcv: boolean,
  includeVolume: boolean,
  periodicity: Periodicity
): Promise<unknown> {
  const fields = getHistoryFields(isOhlcv, includeVolume);

  return fetchHistoryChartData(
    tickers,
    chartDateRange,
    fields,
    CHART_REFERENCE_FIELDS.join(","),
    periodicity
  );
}

async function fetchTickerChartData(
  tickers: string[],
  chartDateRange: ChartDateRange,
  isOhlcv: boolean,
  includeVolume: boolean
): Promise<TickerSeries[]> {
  const ytdDays = chartDateRange === "YTD" ? getYTDInDays() : 0;
  const config = getChartConfig(chartDateRange, isOhlcv, ytdDays);

  let chartData: unknown;

  if (config.source === "intraday") {
    chartData = await fetchIntradayData(
      tickers,
      chartDateRange,
      isOhlcv,
      includeVolume,
      config.intervalMinutes,
      config.tradingDayCount
    );
  } else {
    chartData = await fetchHistoryData(
      tickers,
      chartDateRange,
      isOhlcv,
      includeVolume,
      config.periodicity
    );
  }

  const { data: hydratedData, errors } = hydrateArray(
    chartData as HydrateContent,
    tickerChart
  );

  if (!hydratedData || errors.length > 0) {
    throw new Error(
      `Failed to hydrate ticker chart data for tickers: ${tickers.join(
        ", "
      )}, errors: ${errors?.join("; \n")}`
    );
  }

  return hydratedData as TickerSeries[];
}

const getTickerChartDataQueryKey = (
  tickers: string[],
  chartDateRange: ChartDateRange,
  isOhlcv: boolean,
  includeVolume: boolean
) => [
  "ticker-chart-data",
  chartDateRange,
  tickers.join(","),
  isOhlcv,
  includeVolume,
];

export function useTickerChartData(
  tickers: string[],
  chartDateRange: ChartDateRange,
  includeVolume = false,
  isOhlcv = false
) {
  const ytdDays = useMemo(
    () => (chartDateRange === "YTD" ? getYTDInDays() : 0),
    [chartDateRange]
  );

  const config = getChartConfig(chartDateRange, isOhlcv, ytdDays);

  const query = useQuery({
    queryKey: getTickerChartDataQueryKey(
      tickers,
      chartDateRange,
      isOhlcv,
      includeVolume
    ),
    queryFn: () =>
      fetchTickerChartData(tickers, chartDateRange, isOhlcv, includeVolume),
    enabled: tickers.length > 0,
    staleTime: config.staleTime,
  });

  return {
    ...query,
    dataInterval: config.dataInterval,
    staleTime: config.staleTime,
  };
}
